// frontend/src/utils/notificationCategories.ts
//
// Single source of truth for admin broadcast category metadata.
// Per docs/architecture/notification_system_spec.md §3.1.
//
// Used by Header.tsx (bell rendering), and any future admin analytics
// component that needs to render category-aware UI.

export type AdminBroadcastCategory =
  | 'security'
  | 'maintenance'
  | 'site_status'
  | 'feature_announcement'
  | 'marketing'
  | 'travel_reminder'
  | 'peek_notification';

export interface CategoryMeta {
  /** Human-readable section label */
  label: string;
  /** MUI color string for left border + icon tint */
  color: string;
  /** MUI Icon component name (string — Header imports the icon and looks it up) */
  iconName:
    | 'Security'
    | 'Build'
    | 'Warning'
    | 'Campaign'
    | 'NewReleases'
    | 'FlightTakeoff';
  /** Whether the X button is rendered for this category */
  dismissable: boolean;
  /**
   * Sort priority — lower = higher in list.
   * Per spec §3.3: security → maintenance → site_status → feature_announcement → marketing.
   * travel_reminder lives in the "Your nudges" section, so its priority is irrelevant
   * for the Announcements section but is set high (= low priority) for completeness.
   */
  priority: number;
}

export const CATEGORY_META: Record<AdminBroadcastCategory, CategoryMeta> = {
  security: {
    label: 'Security',
    color: '#D32F2F', // red
    iconName: 'Security',
    dismissable: false,
    priority: 1,
  },
  maintenance: {
    label: 'Maintenance',
    color: '#ED6C02', // amber
    iconName: 'Build',
    dismissable: false,
    priority: 2,
  },
  site_status: {
    label: 'Site Status',
    color: '#D32F2F', // red — outage default; can be tinted by severity field if needed later
    iconName: 'Warning',
    dismissable: false,
    priority: 3,
  },
  feature_announcement: {
    label: 'New Feature',
    color: '#0288D1', // blue/teal
    iconName: 'NewReleases',
    dismissable: true,
    priority: 4,
  },
  marketing: {
    label: 'News',
    color: '#757575', // gray, lower visual weight per spec
    iconName: 'Campaign',
    dismissable: true,
    priority: 5,
  },
  peek_notification: {
    label: 'Take a Peek',
    color: '#2D6E6A',
    iconName: 'FlightTakeoff',
    dismissable: true,
    priority: 7,
  },
  travel_reminder: {
    label: 'Travel Reminder',
    color: '#2D6E6A', // Percoro teal — matches lifecycle nudges
    iconName: 'FlightTakeoff',
    dismissable: true,
    priority: 6,
  },
};

/**
 * Returns metadata for a category. Falls back to feature_announcement if the
 * backend somehow returns an unknown category (defensive — should not happen
 * given the DB CHECK constraint).
 */
export const getCategoryMeta = (category: string): CategoryMeta => {
  return (
    CATEGORY_META[category as AdminBroadcastCategory] ??
    CATEGORY_META.feature_announcement
  );
};

/**
 * Categories that go in the "Announcements" bell section (admin-composed).
 * travel_reminder is admin-categorized but lives in "Your nudges" since
 * it's auto-generated from trip dates, not admin-composed.
 */
export const ANNOUNCEMENT_CATEGORIES: ReadonlyArray<AdminBroadcastCategory> = [
  'security',
  'maintenance',
  'site_status',
  'feature_announcement',
  'marketing',
];
